import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  keyframes,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { motion, AnimatePresence } from "framer-motion";
import { API_URL } from "../../api/apiConfig";
import Matrix6 from "../../img/logos/Matrix6.png";
import { useTranslation } from "react-i18next";
import Ton618InfoDialog from "./Ton618InfoDialog";
import LoginFashionMood from "./LoginFashionMood";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const MotionBox = motion(Box);

const heartBeat = keyframes`
  0% { transform: scale(1); }
  16% { transform: scale(1.035); }
  32% { transform: scale(1); }
  48% { transform: scale(1.055); }
  68% { transform: scale(1); }
  100% { transform: scale(1); }
`;

const heartBeatIcon = keyframes`
  0% { transform: scale(1); }
  16% { transform: scale(1.03); }
  32% { transform: scale(1); }
  48% { transform: scale(1.05); }
  68% { transform: scale(1); }
  100% { transform: scale(1); }
`;

/**
 * Glifos “marcianos” para enmascarar la contraseña (mismo length, valor real intacto).
 * El input sigue siendo type="password": si es type="text", Chrome, Edge y Firefox
 * no ofrecen guardar ni rellenar la contraseña.
 */
const MARTIAN_GLYPHS = [
  "⌬",
  "⏣",
  "◈",
  "◉",
  "◎",
  "⬡",
  "⬢",
  "✦",
  "✧",
  "⚛",
  "⍟",
  "✶",
  "✸",
  "⌖",
  "⊙",
  "⊛",
];

function maskMartian(length) {
  if (!length) return "";
  let out = "";
  for (let i = 0; i < length; i += 1) {
    out += MARTIAN_GLYPHS[i % MARTIAN_GLYPHS.length];
  }
  return out;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 380, damping: 28 },
  },
};

/** Híbrido TON × Artiso: tipografía nuestra, botón / acento Artiso. */
const ACCENT = "#7f9dac";
const ACCENT_DEEP = "#6f8d9c";
const ACCENT_LINK = "#4a6573";
const INK = "#252525";
const FG_MUTED = "#656159";
const HAIRLINE = "#dad8d4";
const SURFACE = "#fbfaf8";
const FONT_SANS = 'Manrope, "Helvetica Neue", Arial, sans-serif';

/** Fibonacci: 13, 21, 34, 55. */
const AUREA = {
  b: "13px",
  c: "21px",
  d: "34px",
  e: "55px",
};

const LoginPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const audioApiRef = useRef({
    unlock: async () => {},
    playSuccess: async () => {},
  });
  const [entrance, setEntrance] = useState(null);
  const [tonInfoOpen, setTonInfoOpen] = useState(false);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce) {
      setEntrance({ reduce: true, x: 0, y: 0 });
      return;
    }
    const rect = el.getBoundingClientRect();
    const wide = window.matchMedia("(min-width: 1200px)").matches;
    const moodX = wide ? window.innerWidth * 0.38 : window.innerWidth / 2;
    const moodY = window.innerHeight * (wide ? 0.48 : 0.18);
    setEntrance({
      reduce: false,
      x: moodX - (rect.left + rect.width / 2),
      y: moodY - (rect.top + rect.height / 2),
    });
  }, []);

  /** Sting corto al entrar (sin ambient cósmico). Archive: LoginPage.cosmos.archive.js */
  useEffect(() => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return undefined;

    const ctx = new AudioCtx();
    let closed = false;
    let holdAudioUntil = 0;

    const unlock = async () => {
      if (closed || ctx.state === "closed") return;
      try {
        if (ctx.state === "suspended") await ctx.resume();
      } catch {
        /* ignore */
      }
    };

    const playSuccess = async () => {
      try {
        if (ctx.state === "closed") return;
        try {
          if (ctx.state === "suspended") await ctx.resume();
        } catch {
          /* ignore */
        }

        const exitDelay = 0.38;
        const now = ctx.currentTime + exitDelay;
        holdAudioUntil = performance.now() + exitDelay * 1000 + 1700;

        const stingBus = ctx.createGain();
        stingBus.gain.value = 0;
        stingBus.connect(ctx.destination);
        stingBus.gain.setValueAtTime(0, now);
        stingBus.gain.linearRampToValueAtTime(1, now + 0.14);
        stingBus.gain.linearRampToValueAtTime(0.35, now + 0.95);
        stingBus.gain.linearRampToValueAtTime(0, now + 1.55);

        const tone = (
          freq,
          when,
          dur,
          { type = "sine", level = 0.2, attack = 0.08, detune = 0 } = {},
        ) => {
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          osc.type = type;
          osc.frequency.setValueAtTime(freq, when);
          if (detune) osc.detune.setValueAtTime(detune, when);
          filter.type = "lowpass";
          filter.frequency.setValueAtTime(900, when);
          filter.frequency.linearRampToValueAtTime(3200, when + dur * 0.55);
          filter.frequency.linearRampToValueAtTime(1200, when + dur);
          g.gain.value = 0;
          osc.connect(filter);
          filter.connect(g);
          g.connect(stingBus);
          g.gain.setValueAtTime(0, when);
          g.gain.linearRampToValueAtTime(level, when + attack);
          g.gain.linearRampToValueAtTime(level * 0.55, when + dur * 0.55);
          g.gain.linearRampToValueAtTime(0, when + dur);
          osc.start(when);
          osc.stop(when + dur + 0.05);
        };

        tone(48, now, 1.35, { type: "sine", level: 0.42, attack: 0.2 });
        tone(72, now + 0.04, 1.3, { type: "sine", level: 0.2, attack: 0.25 });
        tone(96, now + 0.08, 1.15, {
          type: "triangle",
          level: 0.12,
          attack: 0.3,
        });
        tone(192, now + 0.28, 1.05, {
          type: "sine",
          level: 0.16,
          attack: 0.22,
        });
        tone(288, now + 0.34, 1.0, {
          type: "sine",
          level: 0.14,
          attack: 0.25,
          detune: 6,
        });
        tone(384, now + 0.42, 0.95, {
          type: "sine",
          level: 0.11,
          attack: 0.28,
        });
      } catch (err) {
        console.warn("TON login sting failed:", err);
      }
    };

    audioApiRef.current = { unlock, playSuccess };

    void unlock();
    const onGesture = () => {
      void unlock();
    };
    window.addEventListener("pointerdown", onGesture, { passive: true });
    window.addEventListener("touchend", onGesture, { passive: true });
    window.addEventListener("keydown", onGesture);

    return () => {
      closed = true;
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("touchend", onGesture);
      window.removeEventListener("keydown", onGesture);
      audioApiRef.current = {
        unlock: async () => {},
        playSuccess: async () => {},
      };
      const wait = Math.max(0, holdAudioUntil - performance.now());
      window.setTimeout(() => {
        try {
          if (ctx.state !== "closed") ctx.close();
        } catch {
          /* ignore */
        }
      }, wait + 400);
    };
  }, []);

  const pullAutofill = useCallback(() => {
    const u = usernameRef.current?.value ?? "";
    const p = passwordRef.current?.value ?? "";
    if (u) setUsername((prev) => (prev === u ? prev : u));
    if (p) setPassword((prev) => (prev === p ? prev : p));
  }, []);

  useEffect(() => {
    pullAutofill();
    const t1 = window.setTimeout(pullAutofill, 400);
    const t2 = window.setTimeout(pullAutofill, 1200);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [pullAutofill]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    const userValue = usernameRef.current?.value || username;
    const passValue = passwordRef.current?.value || password;
    if (!userValue || !passValue) return;
    setError("");
    setLoading(true);
    void audioApiRef.current.unlock();
    try {
      const response = await axios.post(
        `${API_URL}/auth/token`,
        { username: userValue, password: passValue },
        { headers: { "Content-Type": "application/json" } },
      );
      const { token, user } = response.data;
      localStorage.setItem("token", token);
      if (user?.id) localStorage.setItem("userId", user.id);
      void audioApiRef.current.playSuccess();
      await new Promise((r) => window.setTimeout(r, 320));
      navigate("/post-login");
    } catch (err) {
      console.error("LOGIN ERROR:", err.response?.data || err);
      setError(t("Login.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    width: "100%",
    fontFamily: FONT_SANS,
    "& .MuiInputBase-root": {
      backgroundColor: isDark ? alpha("#fff", 0.05) : alpha(SURFACE, 0.92),
      color: isDark ? "#f2f8fb" : INK,
      minHeight: { xs: 52, sm: 54 },
      borderRadius: 999,
      fontFamily: FONT_SANS,
      boxShadow: "none",
      transition:
        "box-shadow 0.25s ease, background-color 0.25s ease, border-color 0.25s ease",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? alpha("#fff", 0.14) : HAIRLINE,
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? alpha(ACCENT, 0.55) : ACCENT,
    },
    "& .MuiInputBase-root.Mui-focused": {
      backgroundColor: isDark ? alpha(ACCENT, 0.08) : SURFACE,
      boxShadow: `0 0 0 1px ${alpha(ACCENT, 0.35)}`,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ACCENT,
      borderWidth: "1px",
    },
    "& .MuiInputLabel-root": {
      color: isDark ? alpha("#c5e4ee", 0.7) : FG_MUTED,
      fontWeight: 500,
      fontFamily: FONT_SANS,
      letterSpacing: "-0.01em",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: isDark ? ACCENT : ACCENT_LINK,
    },
    "& .MuiInputBase-input": {
      color: isDark ? "#f2f8fb" : INK,
      fontSize: "0.98rem",
      fontWeight: 500,
      fontFamily: FONT_SANS,
      letterSpacing: "-0.01em",
    },
    "& .MuiInputAdornment-root": {
      color: isDark ? alpha(ACCENT, 0.9) : ACCENT_DEEP,
    },
  };

  return (
    <Box
      component="form"
      method="post"
      autoComplete="on"
      onSubmit={handleLogin}
      sx={{
        position: "relative",
        minHeight: "100vh",
        "@supports (min-height: 100dvh)": { minHeight: "100dvh" },
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        alignItems: "center",
        justifyContent: { xs: "flex-start", lg: "flex-end" },
        py: { xs: 2.5, sm: 4 },
        px: { xs: 1.5, sm: 2.5 },
        pt: {
          xs: "max(20px, env(safe-area-inset-top))",
          sm: "max(28px, env(safe-area-inset-top))",
        },
        pb: {
          xs: "max(20px, env(safe-area-inset-bottom))",
          sm: "max(28px, env(safe-area-inset-bottom))",
        },
        pl: "max(12px, env(safe-area-inset-left))",
        pr: "max(12px, env(safe-area-inset-right))",
        overflow: "hidden",
        boxSizing: "border-box",
        fontFamily: FONT_SANS,
        background: "transparent",
      }}
    >
      <LoginFashionMood />

      {/* Espaciador móvil: deja ver la portada arriba */}
      <Box
        aria-hidden
        sx={{
          display: { xs: "block", lg: "none" },
          width: "100%",
          height: { xs: 280, sm: 340 },
          flexShrink: 0,
          position: "relative",
          zIndex: 1,
        }}
      />

      <Container
        maxWidth="sm"
        sx={{
          position: { xs: "relative", lg: "fixed" },
          zIndex: 3,
          right: { lg: "max(28px, 4vw)" },
          top: { lg: "50%" },
          translate: { lg: "0 -50%" },
          width: "100%",
          maxWidth: { xs: "100%", sm: 460, lg: 400 },
          px: { xs: 0, sm: 2, lg: 0 },
          mb: { xs: 0.5, lg: 0 },
        }}
      >
        <MotionBox
          ref={cardRef}
          initial={
            entrance?.reduce
              ? false
              : {
                  x: entrance?.x ?? 0,
                  y: entrance?.y ?? 0,
                  scale: 0.92,
                  opacity: 0,
                }
          }
          animate={
            entrance
              ? { x: 0, y: 0, scale: 1, opacity: 1 }
              : { scale: 0.92, opacity: 0 }
          }
          transition={
            entrance?.reduce
              ? { duration: 0 }
              : {
                  type: "spring",
                  stiffness: 280,
                  damping: 26,
                  opacity: { duration: 0.2 },
                }
          }
          sx={{ width: "100%" }}
        >
          <Box
            sx={{
              position: "relative",
              borderRadius: 4,
              p: "1px",
              background: isDark ? alpha("#fff", 0.14) : alpha(HAIRLINE, 0.95),
              boxShadow: isDark
                ? `0 28px 64px ${alpha("#000", 0.42)}`
                : `0 28px 72px ${alpha("#252525", 0.14)}, 0 0 0 1px ${alpha("#fff", 0.65)}`,
            }}
          >
            <Box
              sx={{
                borderRadius: 3.85,
                overflow: "hidden",
                backdropFilter: "blur(28px) saturate(1.15)",
                background: isDark
                  ? `linear-gradient(165deg, ${alpha("#152030", 0.9)} 0%, ${alpha("#0f1824", 0.95)} 100%)`
                  : `linear-gradient(165deg, ${alpha(SURFACE, 0.98)} 0%, ${alpha("#f7f5f1", 0.99)} 100%)`,
                px: { xs: AUREA.c, sm: AUREA.d },
                py: { xs: AUREA.c, sm: AUREA.d },
                fontFamily: FONT_SANS,
              }}
            >
              <MotionBox
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <MotionBox
                  variants={itemVariants}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: { xs: AUREA.c, sm: AUREA.d },
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setTonInfoOpen(true)}
                    aria-label={t("Login.tonInfoAria")}
                    sx={{
                      position: "relative",
                      width: { xs: 72, sm: 84 },
                      height: { xs: 72, sm: 84 },
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "50%",
                      border: "none",
                      p: 0,
                      cursor: "pointer",
                      background: isDark
                        ? alpha(ACCENT, 0.12)
                        : alpha(ACCENT, 0.1),
                      boxShadow: `0 0 0 1px ${alpha(ACCENT, 0.22)}, 0 10px 28px ${alpha(ACCENT, 0.14)}`,
                      animation: `${heartBeat} 1.85s ease-in-out infinite`,
                      transition: "box-shadow 0.25s ease, transform 0.2s ease",
                      "&:hover": {
                        boxShadow: `0 0 0 1px ${alpha(ACCENT, 0.4)}, 0 12px 36px ${alpha(ACCENT, 0.24)}`,
                      },
                      "&:focus-visible": {
                        outline: `2px solid ${ACCENT}`,
                        outlineOffset: 3,
                      },
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Matrix6}
                      alt=""
                      sx={{
                        width: { xs: 44, sm: 52 },
                        height: { xs: 44, sm: 52 },
                        objectFit: "contain",
                        animation: `${heartBeatIcon} 1.85s ease-in-out infinite`,
                        pointerEvents: "none",
                        "@media (prefers-reduced-motion: reduce)": {
                          animation: "none",
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      component="h1"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: { xs: "0.16em", sm: "0.2em" },
                        fontSize: { xs: "1.55rem", sm: "1.85rem" },
                        lineHeight: 1,
                        color: isDark ? "#f4fbff" : INK,
                        textTransform: "uppercase",
                        m: 0,
                      }}
                    >
                      TON
                    </Typography>
                    <Typography
                      sx={{
                        mt: AUREA.b,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: isDark ? alpha("#c5e4ee", 0.72) : ACCENT_DEEP,
                      }}
                    >
                      {t("Login.moodBrandLine")}
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setTonInfoOpen(true)}
                      sx={{
                        mt: 1.25,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        p: 0.5,
                        color: isDark
                          ? alpha("#9ec8d6", 0.65)
                          : alpha(INK, 0.45),
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        fontFamily: "inherit",
                        transition: "color 0.2s ease",
                        "&:hover": {
                          color: ACCENT_DEEP,
                        },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                      {t("Login.tonInfo")}
                    </Box>
                  </Box>
                </MotionBox>

                <MotionBox
                  aria-hidden
                  variants={itemVariants}
                  sx={{ height: { xs: AUREA.d, sm: AUREA.e } }}
                />

                <MotionBox
                  variants={itemVariants}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: AUREA.c,
                  }}
                >
                  <TextField
                    fullWidth
                    label={t("Login.username")}
                    variant="outlined"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    name="username"
                    id="username"
                    inputRef={usernameRef}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      enterKeyHint: "next",
                      autoCapitalize: "none",
                      autoCorrect: "off",
                      onAnimationStart: () => {
                        requestAnimationFrame(pullAutofill);
                      },
                      onFocus: () => {
                        requestAnimationFrame(pullAutofill);
                      },
                    }}
                    sx={{
                      ...fieldSx,
                      "@keyframes loginAutofillStart": {
                        from: { opacity: 1 },
                        to: { opacity: 1 },
                      },
                      "& .MuiInputBase-input:-webkit-autofill, & .MuiInputBase-input:-webkit-autofill:hover, & .MuiInputBase-input:-webkit-autofill:focus":
                        {
                          animationName: "loginAutofillStart",
                          animationDuration: "0.01s",
                          WebkitBoxShadow: `0 0 0 1000px ${isDark ? "#152030" : SURFACE} inset`,
                          WebkitTextFillColor: isDark ? "#f2f8fb" : INK,
                          caretColor: ACCENT,
                          transition: "background-color 99999s ease-out",
                        },
                    }}
                  />
                  <Box sx={{ position: "relative", width: "100%" }}>
                    <TextField
                      fullWidth
                      label={t("Login.password")}
                      type="password"
                      variant="outlined"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      name="password"
                      id="password"
                      inputRef={passwordRef}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword
                                  ? t("Login.hidePassword")
                                  : t("Login.showPassword")
                              }
                              onClick={() => setShowPassword((v) => !v)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              size="small"
                              disabled={loading}
                              sx={{
                                color: alpha(ACCENT_DEEP, 0.8),
                                "&:hover": { color: ACCENT_DEEP },
                              }}
                            >
                              {showPassword ? (
                                <VisibilityOffOutlinedIcon fontSize="small" />
                              ) : (
                                <VisibilityOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        enterKeyHint: "go",
                        spellCheck: false,
                        autoCapitalize: "none",
                        autoCorrect: "off",
                        onAnimationStart: () => {
                          requestAnimationFrame(pullAutofill);
                        },
                        onFocus: () => {
                          requestAnimationFrame(pullAutofill);
                        },
                        ...(showPassword
                          ? {}
                          : { "aria-label": t("Login.password") }),
                      }}
                      sx={{
                        ...fieldSx,
                        "@keyframes loginAutofillStart": {
                          from: { opacity: 1 },
                          to: { opacity: 1 },
                        },
                        "& .MuiInputBase-input": {
                          ...fieldSx["& .MuiInputBase-input"],
                          color: "transparent",
                          WebkitTextFillColor: "transparent",
                          caretColor: ACCENT,
                          letterSpacing: showPassword ? "normal" : "0.14em",
                        },
                        "& .MuiInputBase-input::selection": {
                          color: "transparent",
                          WebkitTextFillColor: "transparent",
                          backgroundColor: alpha(ACCENT, 0.25),
                        },
                        "& .MuiInputBase-input::-ms-reveal, & .MuiInputBase-input::-ms-clear":
                          {
                            display: "none",
                          },
                        "& .MuiInputBase-input:-webkit-autofill, & .MuiInputBase-input:-webkit-autofill:hover, & .MuiInputBase-input:-webkit-autofill:focus":
                          {
                            animationName: "loginAutofillStart",
                            animationDuration: "0.01s",
                            WebkitTextFillColor: "transparent",
                            caretColor: ACCENT,
                            WebkitBoxShadow: `0 0 0 1000px ${isDark ? "#152030" : SURFACE} inset`,
                            transition: "background-color 99999s ease-out",
                          },
                      }}
                    />
                    {password.length > 0 && (
                      <Box
                        aria-hidden={!showPassword}
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          pl: "46px",
                          pr: "46px",
                          pt: "2px",
                          pointerEvents: "none",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          letterSpacing: showPassword ? "normal" : "0.14em",
                          fontSize: showPassword ? "1.02rem" : "1.05rem",
                          fontWeight: showPassword ? 500 : 700,
                          lineHeight: 1,
                          color: showPassword
                            ? isDark
                              ? "#f2f8fb"
                              : INK
                            : ACCENT_DEEP,
                          fontFamily: showPassword
                            ? "inherit"
                            : '"Segoe UI Symbol","Apple Symbols","Noto Sans Symbols 2",sans-serif',
                          textShadow: showPassword
                            ? "none"
                            : `0 0 10px ${alpha(ACCENT, 0.35)}`,
                        }}
                      >
                        {showPassword ? password : maskMartian(password.length)}
                      </Box>
                    )}
                  </Box>
                </MotionBox>

                <AnimatePresence>
                  {error && (
                    <MotionBox
                      key="login-error"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <Alert
                        severity="error"
                        sx={{
                          borderRadius: 2.5,
                          fontWeight: 600,
                          "& .MuiAlert-message": {
                            overflowWrap: "anywhere",
                          },
                        }}
                      >
                        {error}
                      </Alert>
                    </MotionBox>
                  )}
                </AnimatePresence>

                <MotionBox variants={itemVariants}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading || !username || !password}
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <LoginIcon />
                      )
                    }
                    sx={{
                      mt: 0.5,
                      py: 1.55,
                      minHeight: 50,
                      fontFamily: FONT_SANS,
                      fontWeight: 600,
                      borderRadius: 999,
                      textTransform: "none",
                      fontSize: "0.98rem",
                      letterSpacing: "-0.01em",
                      color: "#171717",
                      background: ACCENT,
                      boxShadow: "none",
                      transition:
                        "transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        background: ACCENT_DEEP,
                        boxShadow: `0 10px 28px ${alpha(ACCENT, 0.28)}`,
                        transform: "translateY(-1px)",
                      },
                      "&:active": { transform: "translateY(0)" },
                      "&.Mui-disabled": {
                        color: alpha("#171717", 0.45),
                        background: alpha(ACCENT, 0.35),
                        boxShadow: "none",
                      },
                    }}
                  >
                    {t("Login.loginButton")}
                  </Button>
                </MotionBox>

                <Typography
                  sx={{
                    textAlign: "center",
                    fontFamily: FONT_SANS,
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    letterSpacing: "-0.01em",
                    mt: AUREA.c,
                    color: isDark ? alpha("#9ec8d6", 0.55) : FG_MUTED,
                  }}
                >
                  {t("Login.secureAccess")}
                </Typography>
              </MotionBox>
            </Box>
          </Box>
        </MotionBox>
      </Container>

      <Ton618InfoDialog
        open={tonInfoOpen}
        onClose={() => setTonInfoOpen(false)}
      />
    </Box>
  );
};

export default LoginPage;
